import {
  BookOpen,
  CalendarDays,
  ChevronDown,
  ChevronUp,
  FileSignature,
  FileText,
  type LucideIcon,
  Package,
  Pencil,
  Receipt,
  ShoppingBag,
  Store,
  Users,
  Wallet,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { useAuthStore } from '../../lib/authStore.js';
import { moduleEnabled, type ModuleKey } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';

export interface ActionDef {
  key: string;
  to: string;
  icon: LucideIcon;
  labelKey: string;
  defaultLabel: string;
  /** Only available if the user holds this permission. */
  perm?: string;
  /** Only available if this toggleable module is enabled. */
  module?: ModuleKey;
}

/** Everything a user can put on their dashboard (also reused by the ⌘K palette). Gated per perm/module. */
export const CATALOG: ActionDef[] = [
  { key: 'new-invoice', to: '/sales/invoices/new', icon: Receipt, labelKey: 'dashboard.newInvoice', defaultLabel: 'New invoice', perm: 'invoices.write', module: 'manual_sales' },
  { key: 'new-bill', to: '/purchasing/bills/new', icon: FileText, labelKey: 'dashboard.newBill', defaultLabel: 'New bill', perm: 'bills.write' },
  { key: 'payments', to: '/payments', icon: Wallet, labelKey: 'dashboard.payments', defaultLabel: 'Payments', perm: 'payments.read' },
  { key: 'items', to: '/inventory/items', icon: Package, labelKey: 'dashboard.items', defaultLabel: 'Items', perm: 'items.read' },
  { key: 'parties', to: '/sales/parties', icon: Users, labelKey: 'dashboard.parties', defaultLabel: 'Customers & Vendors', perm: 'parties.read' },
  { key: 'new-je', to: '/accounting/journals/new', icon: BookOpen, labelKey: 'dashboard.newJE', defaultLabel: 'New journal entry', perm: 'gl.post' },
  { key: 'pos', to: '/pos/register', icon: Store, labelKey: 'dashboard.posRegister', defaultLabel: 'POS register', module: 'pos' },
  { key: 'new-sales-order', to: '/sales/orders/new', icon: ShoppingBag, labelKey: 'dashboard.newSalesOrder', defaultLabel: 'New sales order', perm: 'sales_orders.write', module: 'manual_sales' },
  { key: 'new-quote', to: '/quotations/new', icon: FileSignature, labelKey: 'dashboard.newQuote', defaultLabel: 'New quotation', module: 'quotations' },
  { key: 'installment-plans', to: '/installments/plans', icon: CalendarDays, labelKey: 'dashboard.installmentPlans', defaultLabel: 'Installment plans', module: 'installments', perm: 'installments.read' },
];

const DEFAULT_KEYS = ['new-invoice', 'new-bill', 'payments', 'items', 'parties', 'new-je', 'pos'];

const STORAGE_KEY = 'erp-quick-actions';
function loadSaved(): string[] | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as string[]) : null;
  } catch {
    return null;
  }
}
function saveKeys(keys: string[]): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(keys));
  } catch {
    /* ignore */
  }
}

const byKey = (key: string): ActionDef | undefined => CATALOG.find((a) => a.key === key);

/**
 * Customizable dashboard shortcuts. Users pick which destinations show (incl.
 * POS) and in what order; the choice persists in localStorage. The catalog is
 * gated by permission + enabled module, so the editor only ever offers — and
 * the grid only ever renders — actions the user can actually reach.
 */
export function QuickActions(): JSX.Element {
  const { t } = useTranslation();
  const tenant = useAuthStore((s) => s.tenant);
  const permissions = useAuthStore((s) => s.permissions);
  const enabledModules = useEnabledModules(tenant?.id ?? null);

  const available = useMemo(
    () =>
      CATALOG.filter(
        (a) =>
          (!a.perm || permissions.includes(a.perm)) &&
          (!a.module || moduleEnabled(enabledModules, a.module)),
      ),
    [permissions, enabledModules],
  );
  const availableKeys = useMemo(() => new Set(available.map((a) => a.key)), [available]);

  const [selected, setSelected] = useState<string[]>(() => loadSaved() ?? DEFAULT_KEYS);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string[]>([]);

  const label = (a: ActionDef): string => t(a.labelKey, { defaultValue: a.defaultLabel });

  // Visible tiles: saved selection, in order, minus anything no longer available.
  const visible = selected
    .filter((k) => availableKeys.has(k))
    .map(byKey)
    .filter((a): a is ActionDef => !!a);

  const openEditor = (): void => {
    setDraft(selected.filter((k) => availableKeys.has(k)));
    setEditing(true);
  };
  const toggle = (key: string): void =>
    setDraft((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const move = (key: string, dir: -1 | 1): void =>
    setDraft((prev) => {
      const i = prev.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j]!, next[i]!];
      return next;
    });
  const save = (): void => {
    setSelected(draft);
    saveKeys(draft);
    setEditing(false);
  };

  const draftSelected = draft.filter((k) => availableKeys.has(k));
  const draftUnselected = available.filter((a) => !draft.includes(a.key));

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold">
          {t('dashboard.quickActions', { defaultValue: 'Quick actions' })}
        </h2>
        <Button variant="ghost" size="sm" onClick={openEditor}>
          <Pencil className="h-4 w-4" aria-hidden />
          {t('actions.customize', { defaultValue: 'Customize' })}
        </Button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {visible.map((a) => {
          const Icon = a.icon;
          return (
            <Link key={a.key} to={a.to} className="group block">
              <div className="flex flex-col items-center gap-2.5 rounded-lg border border-border bg-bg py-5 text-center text-sm font-medium shadow-sm transition duration-200 ease-out hover:-translate-y-0.5 hover:border-brand hover:shadow-md active:scale-[0.98] motion-reduce:transform-none">
                <span className="grid h-10 w-10 place-items-center rounded-md bg-brand/10 text-brand transition-transform duration-200 group-hover:scale-110">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                {label(a)}
              </div>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={openEditor}
          className="flex min-h-[104px] flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-border text-sm font-medium text-fg-muted transition duration-200 ease-out hover:-translate-y-0.5 hover:border-brand hover:text-brand active:scale-[0.98] motion-reduce:transform-none"
        >
          <Pencil className="h-5 w-5" aria-hidden />
          {t('actions.customize', { defaultValue: 'Customize' })}
        </button>
      </div>

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.customizeQuickActions', { defaultValue: 'Customize quick actions' })}
            </DialogTitle>
          </DialogHeader>

          <div className="flex max-h-[55vh] flex-col gap-1 overflow-y-auto">
            {draftSelected.map((k, idx) => {
              const a = byKey(k)!;
              const Icon = a.icon;
              return (
                <div
                  key={k}
                  className="flex items-center gap-2 rounded-md border border-border bg-bg-subtle px-2.5 py-2"
                >
                  <input
                    type="checkbox"
                    checked
                    onChange={() => toggle(k)}
                    className="h-4 w-4 accent-[var(--color-brand)]"
                    aria-label={label(a)}
                  />
                  <Icon className="h-4 w-4 flex-none text-brand" aria-hidden />
                  <span className="flex-1 text-sm">{label(a)}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={idx === 0}
                    onClick={() => move(k, -1)}
                    aria-label={t('actions.moveUp', { defaultValue: 'Move up' })}
                  >
                    <ChevronUp className="h-4 w-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={idx === draftSelected.length - 1}
                    onClick={() => move(k, 1)}
                    aria-label={t('actions.moveDown', { defaultValue: 'Move down' })}
                  >
                    <ChevronDown className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              );
            })}

            {draftUnselected.length > 0 && (
              <p className="mt-2 px-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">
                {t('dashboard.moreActions', { defaultValue: 'Add more' })}
              </p>
            )}
            {draftUnselected.map((a) => {
              const Icon = a.icon;
              return (
                <label
                  key={a.key}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 hover:bg-bg-subtle"
                >
                  <input
                    type="checkbox"
                    checked={false}
                    onChange={() => toggle(a.key)}
                    className="h-4 w-4 accent-[var(--color-brand)]"
                  />
                  <Icon className="h-4 w-4 flex-none text-fg-muted" aria-hidden />
                  <span className="flex-1 text-sm text-fg-muted">{label(a)}</span>
                </label>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditing(false)}>
              {t('actions.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={save}>{t('actions.save', { defaultValue: 'Save' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
